import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Modal, SelectMenu, TableColumn, Typography } from '@scale/scaleui';
import cn from 'classnames';
import { useCallback, useState } from 'react';
import { DragDropContext, Draggable, DropResult, Droppable } from 'react-beautiful-dnd';

// TODO(albert): Table column ordering should really be a part of the Table component itself.
// For now, let's add it as a separate component here until we can figure out a usable design.

interface TableColumnPreset {
  id: string;
  label: string;
  columns: string[];
}

interface TableColumnDisplayOrderConfigProps {
  context: {
    queryKey: string;
    orderedColumns: TableColumn[];
    isColumnVisible: (columnId: string) => boolean;
    toggleVisibility: (columnId: string) => void;
    showOnlyColumns: (visibleColumns: string[]) => void;
    setColumnOrderingFromList: (columnIds: string[]) => void;
    reset: () => void;
  };
  presets?: TableColumnPreset[];
}

export function TableColumnDisplayOrderConfig({
  context,
  presets = [],
}: TableColumnDisplayOrderConfigProps) {
  const {
    queryKey,
    orderedColumns,
    isColumnVisible,
    toggleVisibility,
    setColumnOrderingFromList,
    showOnlyColumns,
    reset,
  } = context;
  const handleDragEnd = useCallback(
    (result: DropResult) => {
      if (!result.destination) {
        return;
      }
      const startIndex = result.source.index;
      const endIndex = result.destination.index;
      const newColumnDisplayOrder = orderedColumns.map(column => column.id);
      const [removed] = newColumnDisplayOrder.splice(startIndex, 1);
      newColumnDisplayOrder.splice(endIndex, 0, removed);
      setColumnOrderingFromList(newColumnDisplayOrder);
    },
    [orderedColumns, setColumnOrderingFromList],
  );

  const getItemStyle = (isDragging: boolean, draggableStyle: any) => ({
    userSelect: 'none',
    opacity: isDragging ? 0.1 : 1.0,
    ...draggableStyle,
  });

  const [customModalOpen, setCustomModalOpen] = useState<boolean>(false);

  const handleColumnDisplayMenu = useCallback(
    (option: any) => {
      const preset = presets.find(preset => preset.id === option);
      switch (option) {
        case 'default':
          reset();
          break;
        case 'custom':
          setCustomModalOpen(true);
          break;
        default:
          if (preset) {
            showOnlyColumns(preset.columns);
          }
      }
    },
    [presets, reset, showOnlyColumns],
  );

  return (
    <>
      <Modal
        title="Custom Display Order"
        open={customModalOpen}
        onClose={() => setCustomModalOpen(false)}
      >
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId={`droppable-column-display-order-${queryKey}`}>
            {provided => (
              <div
                className="flex flex-col gap-1"
                {...provided.droppableProps}
                ref={provided.innerRef}
              >
                {orderedColumns.map((column, index) => (
                  <Draggable key={column.id} draggableId={column.id} index={index}>
                    {(provided, snapshot) => (
                      <div
                        className={cn(
                          'flex items-center justify-between p-2 rounded-md border border-neutral-200 bg-neutral-0 gap-2',
                          {
                            'bg-neutral-100': snapshot.isDragging,
                          },
                        )}
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        style={getItemStyle(snapshot.isDragging, provided.draggableProps.style)}
                      >
                        <div className="flex gap-2 items-center">
                          <FontAwesomeIcon
                            icon="grip-vertical"
                            className="text-neutral-500 text-xs"
                          />
                          <Typography.Subtitle4>{column.header}</Typography.Subtitle4>
                        </div>
                        <div className="flex gap-2 items-center w-4">
                          <FontAwesomeIcon
                            icon={isColumnVisible(column.id) ? 'eye' : 'eye-slash'}
                            className="text-neutral-500 text-xs"
                            onClick={() => toggleVisibility(column.id)}
                          />
                        </div>
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </Modal>
      <SelectMenu
        variant="minimal"
        options={[
          {
            id: 'none',
            name: 'Column Display Order',
          },
          {
            id: 'default',
            name: 'Show all (default)',
          },
          ...presets.map(preset => ({
            id: preset.id,
            name: preset.label,
          })),
          {
            id: 'custom',
            name: 'Customize columns',
          },
        ]}
        selectedOptionId="none"
        multiple={false}
        setSelected={handleColumnDisplayMenu}
      />
    </>
  );
}
